import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  FileSpreadsheet,
  Plus,
  Search,
  ShieldCheck,
  Download,
  Printer,
  RefreshCw,
  XCircle,
  Pencil,
  Trash2,
} from "lucide-react";
import { api, formatErr } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { toast } from "sonner";
import "./salonWeb.css";

const BASE = "/client/salon/accounting";
const GST_STATE_BY_CODE = {
  "01": "Jammu and Kashmir",
  "02": "Himachal Pradesh",
  "03": "Punjab",
  "04": "Chandigarh",
  "05": "Uttarakhand",
  "06": "Haryana",
  "07": "Delhi",
  "08": "Rajasthan",
  "09": "Uttar Pradesh",
  10: "Bihar",
  11: "Sikkim",
  12: "Arunachal Pradesh",
  13: "Nagaland",
  14: "Manipur",
  15: "Mizoram",
  16: "Tripura",
  17: "Meghalaya",
  18: "Assam",
  19: "West Bengal",
  20: "Jharkhand",
  21: "Odisha",
  22: "Chhattisgarh",
  23: "Madhya Pradesh",
  24: "Gujarat",
  26: "Dadra and Nagar Haveli and Daman and Diu",
  27: "Maharashtra",
  29: "Karnataka",
  30: "Goa",
  31: "Lakshadweep",
  32: "Kerala",
  33: "Tamil Nadu",
  34: "Puducherry",
  35: "Andaman and Nicobar Islands",
  36: "Telangana",
  37: "Andhra Pradesh",
  38: "Ladakh",
  97: "Other Territory",
};
const stateFromGstin = (gstin) =>
  GST_STATE_BY_CODE[String(gstin || "").slice(0, 2)] || "";
const groups = [
  ["Overview", [["Dashboard", "dashboard"]]],
  [
    "Masters",
    [
      ["Chart of Accounts", "ledgers"],
      ["Customers & Suppliers", "parties"],
    ],
  ],
  [
    "Vouchers",
    [
      ["Sales", "sales"],
      ["Purchase", "purchase"],
      ["Receipt", "receipt"],
      ["Payment", "payment"],
      ["Contra", "contra"],
      ["Journal", "journal"],
      ["Credit Note", "credit-note"],
      ["Debit Note", "debit-note"],
    ],
  ],
  [
    "Reports",
    [
      ["Trial Balance", "trial-balance"],
      ["General Ledger", "general-ledger"],
      ["Day Book", "day-book"],
      ["Cash Book", "cash-book"],
      ["Bank Book", "bank-book"],
      ["Profit & Loss", "profit-loss"],
      ["Balance Sheet", "balance-sheet"],
    ],
  ],
  [
    "GST",
    [
      ["GST Dashboard", "gst"],
      ["GSTR-1 Working", "gstr-1"],
      ["GSTR-3B Working", "gstr-3b"],
      ["GSTR-2B Reconciliation", "gstr-2b"],
    ],
  ],
  [
    "Control",
    [
      ["Bank Reconciliation", "bank-reconciliation"],
      ["Audit Trail", "audit-trail"],
      ["CA Access", "ca-access"],
      ["Settings", "settings"],
    ],
  ],
];
const voucherMap = {
  sales: "SALES",
  purchase: "PURCHASE",
  receipt: "RECEIPT",
  payment: "PAYMENT",
  contra: "CONTRA",
  journal: "JOURNAL",
  "credit-note": "CREDIT_NOTE",
  "debit-note": "DEBIT_NOTE",
};
const money = (v) =>
  `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);

function AccountingChrome({ children }) {
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(null);
  return (
    <div className="salon-web-shell salon-accounts-shell min-h-screen bg-[#F7F8FA] text-[#172033]">
      <div className="border-b bg-white px-4 py-2">
        <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link
              to="/client/storefront"
              className="text-xs font-semibold text-[#566176] hover:text-black"
            >
              <ArrowLeft className="mr-1 inline h-3.5 w-3.5" />
              Salon workspace
            </Link>
            <span className="text-[#D5D9E2]">|</span>
            <div>
              <h1 className="text-base font-bold">Salon Accounts</h1>
              <p className="text-[10px] text-[#687386]">
                Books, GST working papers and audit controls
              </p>
            </div>
          </div>
          <div className="text-right text-[10px] uppercase tracking-wider text-[#687386]">
            Financial workspace
            <br />
            <strong className="text-[#172033]">India · INR</strong>
          </div>
        </div>
      </div>
      <div className="border-b bg-white">
        <div className="relative z-20 mx-auto flex max-w-[1500px] flex-wrap gap-1 overflow-visible px-4 py-1.5">
          {groups.map(([name, items]) => (
            <div
              className="relative shrink-0"
              key={name}
              onMouseEnter={() => setOpenMenu(name)}
              onMouseLeave={() => setOpenMenu(null)}
            >
              <Link
                to={`${BASE}/${items[0][1]}`}
                onFocus={() => setOpenMenu(name)}
                onClick={() => setOpenMenu(null)}
                className={`rounded-sm px-3 py-1.5 text-xs font-semibold ${items.some(([, p]) => location.pathname.endsWith(`/${p}`)) ? "bg-[#172033] text-white" : "hover:bg-[#EEF1F5]"}`}
              >
                {name}
              </Link>
              <div
                className={`${openMenu === name ? "visible opacity-100" : "invisible opacity-0"} absolute left-0 top-full z-30 min-w-[210px] border bg-white py-1 shadow-lg transition-opacity`}
              >
                {items.map(([label, path]) => (
                  <Link
                    className="block px-3 py-2 text-xs hover:bg-[#F1F3F6]"
                    to={`${BASE}/${path}`}
                    key={path}
                    onClick={() => setOpenMenu(null)}
                  >
                    {label}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <main className="mx-auto max-w-[1500px] p-4">{children}</main>
    </div>
  );
}

function Dashboard() {
  const [data, setData] = useState({});
  useEffect(() => {
    api
      .get("/client/salon/books/dashboard")
      .then((r) => setData(r.data || {}))
      .catch((e) => toast.error(formatErr(e.response?.data?.detail)));
  }, []);
  const cards = [
    ["Sales", data.sales],
    ["Purchases", data.purchases],
    ["Receipts", data.receipts],
    ["Payments", data.payments],
    ["Net movement", data.net],
  ];
  return (
    <>
      <div className="mb-3 flex items-end justify-between">
        <div>
          <h2 className="text-lg font-bold">Accounting dashboard</h2>
          <p className="text-xs text-[#687386]">
            Current financial-year snapshot
          </p>
        </div>
        <Link to={`${BASE}/sales`}>
          <Button size="sm" className="h-8 rounded-sm">
            <Plus className="mr-1 h-4 w-4" />
            Sales voucher
          </Button>
        </Link>
      </div>
      <div className="grid grid-cols-2 border border-[#DDE1E8] bg-white md:grid-cols-5">
        {cards.map(([l, v], i) => (
          <div key={l} className={`${i ? "border-l" : ""} p-3`}>
            <div className="text-[10px] uppercase tracking-wide text-[#687386]">
              {l}
            </div>
            <div className="mt-1 font-mono text-lg font-bold">{money(v)}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 grid gap-3 lg:grid-cols-3">
        <Quick
          title="Daily posting"
          icon={BookOpen}
          items={[
            ["Sales invoice", "sales"],
            ["Purchase bill", "purchase"],
            ["Receipt voucher", "receipt"],
            ["Payment voucher", "payment"],
          ]}
        />
        <Quick
          title="Compliance"
          icon={FileSpreadsheet}
          items={[
            ["GST dashboard", "gst"],
            ["GSTR-1 working", "gstr-1"],
            ["GSTR-3B working", "gstr-3b"],
            ["Purchase / GSTR-2B working", "gstr-2b"],
          ]}
        />
        <Quick
          title="Controls"
          icon={ShieldCheck}
          items={[
            ["Trial balance", "trial-balance"],
            ["Bank reconciliation", "bank-reconciliation"],
            ["Audit trail", "audit-trail"],
            ["CA access", "ca-access"],
          ]}
        />
      </div>
    </>
  );
}
function Quick({ title, icon: Icon, items }) {
  return (
    <section className="border bg-white">
      <div className="flex items-center gap-2 border-b bg-[#F4F6F8] px-3 py-2 text-xs font-bold">
        <Icon className="h-4 w-4" />
        {title}
      </div>
      <div className="grid grid-cols-2">
        {items.map(([l, p]) => (
          <Link
            key={p}
            to={`${BASE}/${p}`}
            className="border-b border-r px-3 py-2 text-xs hover:bg-[#F8FAFC]"
          >
            {l}
          </Link>
        ))}
      </div>
    </section>
  );
}

function VoucherPage({ kind }) {
  const [rows, setRows] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [parties, setParties] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [settings, setSettings] = useState({
    state: "",
    gst_registered: false,
    address: "",
    pan: "",
    books_locked_through: "",
  });
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const empty = {
    voucher_date: today(),
    party_name: "",
    party_gstin: "",
    invoice_number: "",
    reference_number: "",
    narration: "",
    debit_ledger_id: "",
    credit_ledger_id: "",
    amount: "",
    taxable_value: "",
    gst_rate: "0",
    cgst: "0",
    sgst: "0",
    igst: "0",
    place_of_supply: "",
    party_id: "",
    inventory_item_id: "",
    quantity: "",
  };
  const [form, setForm] = useState(empty);
  const type = voucherMap[kind];
  const title = kind
    .split("-")
    .map((x) => x[0].toUpperCase() + x.slice(1))
    .join(" ");
  const load = useCallback(
    () =>
      Promise.all([
        api.get(`/client/salon/books/vouchers?type=${type}`),
        api.get("/client/salon/books/ledgers"),
        api.get("/client/salon/books/parties"),
        api.get("/client/salon/books/settings"),
        api.get("/client/salon/inventory/items"),
      ])
        .then(([a, b, c, d, e]) => {
          setRows(a.data || []);
          setLedgers(b.data || []);
          setParties(c.data || []);
          setSettings(d.data || {});
          setInventoryItems(e.data || []);
        })
        .catch((e) => toast.error(formatErr(e.response?.data?.detail))),
    [type],
  );
  useEffect(() => {
    load();
  }, [load]);
  const isPurchase = ["PURCHASE", "DEBIT_NOTE"].includes(type);
  const isSales = ["SALES", "CREDIT_NOTE"].includes(type);
  const isTaxVoucher = isPurchase || isSales;
  const selectedParty = parties.find((p) => p.id === form.party_id);
  const selectedInventoryItem = inventoryItems.find(
    (item) => item.id === form.inventory_item_id,
  );
  const partyOptions = parties.filter((p) =>
    isPurchase
      ? ["SUPPLIER", "BOTH"].includes(p.party_type)
      : isSales
        ? ["CUSTOMER", "BOTH"].includes(p.party_type)
        : true,
  );
  const accountOptions = ledgers.filter((l) =>
    isPurchase
      ? [
          "Inventory",
          "Fixed Assets",
          "Direct Expenses",
          "Operating Expenses",
        ].includes(l.group)
      : isSales
        ? l.group === "Revenue"
        : true,
  );
  const cashOptions = ledgers.filter((l) => l.group === "Cash & Bank");
  const primaryOptions = isTaxVoucher
    ? accountOptions
    : type === "RECEIPT"
      ? cashOptions
      : type === "PAYMENT"
        ? ledgers.filter(
            (l) =>
              !["Cash & Bank", "GST Input Credit", "GST Payable"].includes(
                l.group,
              ),
          )
        : ledgers;
  const secondaryOptions = isTaxVoucher
    ? accountOptions
    : type === "PAYMENT"
      ? cashOptions
      : ledgers;
  const taxable = Number(form.taxable_value || 0);
  const rate = Number(selectedInventoryItem?.gst_rate ?? form.gst_rate ?? 0);
  const taxTotal =
    settings.gst_registered && isTaxVoucher
      ? Number(((taxable * rate) / 100).toFixed(2))
      : 0;
  const intraState =
    selectedParty &&
    settings.state &&
    selectedParty.state?.trim().toLowerCase() ===
      settings.state.trim().toLowerCase();
  const autoCgst = intraState ? Number((taxTotal / 2).toFixed(2)) : 0;
  const autoSgst = intraState ? Number((taxTotal - autoCgst).toFixed(2)) : 0;
  const autoIgst = intraState ? 0 : taxTotal;
  const voucherTotal = isTaxVoucher
    ? Number((taxable + taxTotal).toFixed(2))
    : Number(form.amount || 0);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/client/salon/books/vouchers", {
        ...form,
        voucher_type: type,
        amount: voucherTotal,
        taxable_value: Number(form.taxable_value || form.amount),
        gst_rate: Number(form.gst_rate || 0),
        cgst: autoCgst,
        sgst: autoSgst,
        igst: autoIgst,
        quantity: Number(form.quantity || 0),
      });
      toast.success(`${title} voucher posted`);
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };
  const visible = rows.filter((r) =>
    JSON.stringify(r).toLowerCase().includes(search.toLowerCase()),
  );
  const cancelVoucher = async (row) => {
    if (!window.confirm(`Cancel voucher ${row.voucher_number}? The audit record will be retained.`)) return;
    try { await api.post(`/client/salon/books/vouchers/${row.id}/cancel`); toast.success("Voucher cancelled"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold">
            {open ? `New ${title} Voucher` : `${title} Register`}
          </h2>
          <p className="text-xs text-[#687386]">
            {open
              ? "Enter the voucher in the full-width accounting workspace"
              : `Posted ${title.toLowerCase()} vouchers with balanced debit and credit`}
          </p>
        </div>
        {!open && (
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-2 h-4 w-4 text-[#8892A3]" />
              <Input
                className="h-8 w-56 rounded-sm pl-8 text-xs"
                placeholder="Search register"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Button
              size="sm"
              className="h-8 rounded-sm"
              onClick={() => setOpen(true)}
            >
              <Plus className="mr-1 h-4 w-4" />
              New {title}
            </Button>
          </div>
        )}
      </div>
      {!open && (
        <div className="border bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F1F3F6]">
                <TableHead>Date</TableHead>
                <TableHead>Voucher No.</TableHead>
                <TableHead>Invoice / Ref.</TableHead>
                <TableHead>Party</TableHead>
                <TableHead>Narration</TableHead>
                <TableHead className="text-right">Taxable</TableHead>
                <TableHead className="text-right">GST</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="py-2 font-mono text-xs">
                    {r.voucher_date}
                  </TableCell>
                  <TableCell className="py-2 text-xs font-semibold">
                    {r.voucher_number}
                  </TableCell>
                  <TableCell className="py-2 text-xs">
                    {r.invoice_number || r.reference_number || "—"}
                  </TableCell>
                  <TableCell className="py-2 text-xs">
                    {r.party_name || "—"}
                  </TableCell>
                  <TableCell className="py-2 text-xs">
                    {r.narration || "—"}
                  </TableCell>
                  <TableCell className="py-2 text-right font-mono text-xs">
                    {money(r.taxable_value)}
                  </TableCell>
                  <TableCell className="py-2 text-right font-mono text-xs">
                    {money(
                      Number(r.cgst || 0) +
                        Number(r.sgst || 0) +
                        Number(r.igst || 0),
                    )}
                  </TableCell>
                  <TableCell className="py-2 text-right font-mono text-xs font-bold">
                    {money(r.amount)}
                  </TableCell>
                  <TableCell className="py-2"><Button type="button" size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => cancelVoucher(r)}><XCircle className="mr-1 h-3.5 w-3.5" />Cancel</Button></TableCell>
                </TableRow>
              ))}
              {!visible.length && (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="py-12 text-center text-xs text-[#687386]"
                  >
                    No {title.toLowerCase()} vouchers posted.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
      {open && (
        <section className="border border-[#D8DDE6] bg-white">
          <div className="border-b bg-[#F1F3F6] px-4 py-2 text-sm font-bold">
            {title} Voucher Entry
          </div>
          <form
            onSubmit={save}
            className="grid grid-cols-2 gap-4 p-4 md:grid-cols-4"
          >
            <Field label="Voucher date">
              <Input
                type="date"
                required
                value={form.voucher_date}
                onChange={(e) =>
                  setForm({ ...form, voucher_date: e.target.value })
                }
              />
            </Field>
            <Field label="Invoice number">
              <Input
                value={form.invoice_number}
                onChange={(e) =>
                  setForm({ ...form, invoice_number: e.target.value })
                }
              />
            </Field>
            <Field label="Reference">
              <Input
                value={form.reference_number}
                onChange={(e) =>
                  setForm({ ...form, reference_number: e.target.value })
                }
              />
            </Field>
            <Field
              label={isPurchase ? "Supplier" : isSales ? "Customer" : "Party"}
            >
              <select
                required={isTaxVoucher}
                className="mt-1 h-10 w-full border bg-white px-2 text-sm"
                value={form.party_id}
                onChange={(e) => {
                  const party = parties.find((p) => p.id === e.target.value);
                  setForm({
                    ...form,
                    party_id: e.target.value,
                    party_name: party?.name || "",
                    party_gstin: party?.gstin || "",
                    place_of_supply: party?.state || "",
                    ...(isPurchase
                      ? { credit_ledger_id: party?.ledger_id || "" }
                      : isSales
                        ? { debit_ledger_id: party?.ledger_id || "" }
                        : {}),
                  });
                }}
              >
                <option value="">
                  Select{" "}
                  {isPurchase ? "supplier" : isSales ? "customer" : "party"}
                </option>
                {partyOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.gstin ? ` · ${p.gstin}` : " · Unregistered"}
                  </option>
                ))}
              </select>
            </Field>
            {isPurchase && (
              <Field label="Inventory item">
                <select
                  required
                  className="mt-1 h-10 w-full border bg-white px-2 text-sm"
                  value={form.inventory_item_id}
                  onChange={(e) => {
                    const item = inventoryItems.find(
                      (row) => row.id === e.target.value,
                    );
                    setForm({
                      ...form,
                      inventory_item_id: e.target.value,
                      gst_rate: String(item?.gst_rate || 0),
                      narration: item
                        ? `${item.name}${item.hsn_sac ? ` · HSN ${item.hsn_sac}` : ""}`
                        : form.narration,
                    });
                  }}
                >
                  <option value="">Select purchased item</option>
                  {inventoryItems.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} · GST {Number(item.gst_rate || 0)}%
                      {item.hsn_sac ? ` · HSN ${item.hsn_sac}` : ""}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            {isPurchase && (
              <Field label="Quantity purchased">
                <Input type="number" min="0.001" step="0.001" required value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
              </Field>
            )}
            <Field label="GST treatment">
              <Input
                readOnly
                value={
                  !settings.gst_registered
                    ? "GST not enabled in Settings"
                    : !selectedParty
                      ? "Select party"
                      : intraState
                        ? `Intra-state · CGST ${autoCgst.toFixed(2)} + SGST ${autoSgst.toFixed(2)}`
                        : `Inter-state · IGST ${autoIgst.toFixed(2)}`
                }
              />
            </Field>
            <Field label="Place of supply">
              <Input
                readOnly
                value={selectedParty?.state || "From party master"}
              />
            </Field>
            {!isTaxVoucher && (
              <Field
                label={
                  isPurchase
                    ? "Purchase / expense account"
                    : isSales
                      ? "Sales account"
                      : "Debit account"
                }
              >
                <LedgerSelect
                  required
                  ledgers={
                    isPurchase || isSales ? accountOptions : primaryOptions
                  }
                  value={isSales ? form.credit_ledger_id : form.debit_ledger_id}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      [isSales ? "credit_ledger_id" : "debit_ledger_id"]:
                        e.target.value,
                    })
                  }
                />
              </Field>
            )}
            {!isTaxVoucher && (
              <Field label="Credit account">
                <LedgerSelect
                  required
                  ledgers={secondaryOptions}
                  value={form.credit_ledger_id}
                  onChange={(e) =>
                    setForm({ ...form, credit_ledger_id: e.target.value })
                  }
                />
              </Field>
            )}
            <Field label="Voucher total">
              <Input
                required
                type="number"
                min="0.01"
                step="0.01"
                readOnly={isTaxVoucher}
                value={isTaxVoucher ? voucherTotal : form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </Field>
            {isTaxVoucher && (
              <Field label="Taxable value">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.taxable_value}
                  onChange={(e) =>
                    setForm({ ...form, taxable_value: e.target.value })
                  }
                />
              </Field>
            )}
            {isTaxVoucher && (
              <Field label="GST rate">
                {isPurchase ? (
                  <Input readOnly value={`${rate}% · from inventory item`} />
                ) : (
                  <select
                    className="mt-1 h-10 w-full border bg-white px-2 text-sm"
                    value={form.gst_rate}
                    onChange={(e) =>
                      setForm({ ...form, gst_rate: e.target.value })
                    }
                  >
                    {[0, 5, 12, 18, 28].map((r) => (
                      <option key={r} value={r}>
                        {r}%
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            )}
            {isTaxVoucher && (
              <div className="grid grid-cols-3 gap-1">
                <Field label="CGST">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    readOnly
                    value={autoCgst}
                  />
                </Field>
                <Field label="SGST">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    readOnly
                    value={autoSgst}
                  />
                </Field>
                <Field label="IGST">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    readOnly
                    value={autoIgst}
                  />
                </Field>
              </div>
            )}
            <div className="col-span-2 md:col-span-3">
              <Field label="Narration">
                <Input
                  value={form.narration}
                  onChange={(e) =>
                    setForm({ ...form, narration: e.target.value })
                  }
                />
              </Field>
            </div>
            <DialogFooter className="col-span-2 border-t pt-3 md:col-span-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">Post voucher</Button>
            </DialogFooter>
          </form>
        </section>
      )}
    </>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wide text-[#596579]">
        {label}
      </Label>
      {children}
    </div>
  );
}
function LedgerSelect({ ledgers, ...props }) {
  return (
    <select
      className="mt-1 h-10 w-full border border-input bg-white px-2 text-sm"
      {...props}
    >
      <option value="">Select ledger</option>
      {ledgers.map((l) => (
        <option key={l.id} value={l.id}>
          {l.name} · {l.group}
        </option>
      ))}
    </select>
  );
}

function Ledgers() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/client/salon/books/ledgers").then((r) => setRows(r.data || []));
  }, []);
  return (
    <>
      <h2 className="mb-3 text-lg font-bold">Chart of Accounts</h2>
      <div className="border bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F1F3F6]">
              <TableHead>Code</TableHead>
              <TableHead>Ledger</TableHead>
              <TableHead>Group</TableHead>
              <TableHead>Nature</TableHead>
              <TableHead>GST role</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs">{r.code}</TableCell>
                <TableCell className="text-xs font-semibold">
                  {r.name}
                </TableCell>
                <TableCell className="text-xs">{r.group}</TableCell>
                <TableCell className="text-xs">{r.nature}</TableCell>
                <TableCell className="text-xs">{r.gst_role || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
function TrialBalance() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api
      .get("/client/salon/books/trial-balance")
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(formatErr(e.response?.data?.detail)));
  }, []);
  const td = rows.reduce((s, r) => s + Number(r.debit || 0), 0),
    tc = rows.reduce((s, r) => s + Number(r.credit || 0), 0);
  return (
    <>
      <h2 className="mb-3 text-lg font-bold">Trial Balance</h2>
      <div className="border bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F1F3F6]">
              <TableHead>Ledger</TableHead>
              <TableHead>Group</TableHead>
              <TableHead className="text-right">Debit</TableHead>
              <TableHead className="text-right">Credit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.ledger_id}>
                <TableCell className="text-xs font-semibold">
                  {r.name}
                </TableCell>
                <TableCell className="text-xs">{r.group}</TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {money(r.debit)}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {money(r.credit)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-[#F1F3F6] font-bold">
              <TableCell colSpan={2}>Total</TableCell>
              <TableCell className="text-right font-mono">
                {money(td)}
              </TableCell>
              <TableCell className="text-right font-mono">
                {money(tc)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </>
  );
}
function Parties() {
  const empty = {
    party_type: "CUSTOMER",
    name: "",
    phone: "",
    email: "",
    gstin: "",
    address: "",
    state: "",
    opening_balance: "0",
    balance_type: "RECEIVABLE",
  };
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState("");
  const [search, setSearch] = useState("");
  const load = useCallback(
    () =>
      api
        .get("/client/salon/books/parties")
        .then((r) => setRows(r.data || []))
        .catch((e) => toast.error(formatErr(e.response?.data?.detail))),
    [],
  );
  useEffect(() => {
    load();
  }, [load]);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api[editId ? "put" : "post"](editId ? `/client/salon/books/parties/${editId}` : "/client/salon/books/parties", {
        ...form,
        opening_balance: Number(form.opening_balance || 0),
      });
      toast.success(editId ? "Party updated" : "Party added");
      setOpen(false);
      setEditId("");
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };
  const visible = rows.filter((r) =>
    `${r.name} ${r.phone} ${r.email} ${r.gstin}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  const editParty = (row) => { setEditId(row.id); setForm({ ...empty, ...row, opening_balance: String(row.opening_balance || 0) }); setOpen(true); };
  const removeParty = async (row) => { if (!window.confirm(`Remove ${row.name}?`)) return; try { await api.delete(`/client/salon/books/parties/${row.id}`); toast.success("Party removed"); load(); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } };
  return (
    <>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold">Customers & Suppliers</h2>
          <p className="text-xs text-[#687386]">
            Party masters with automatically linked accounting ledgers
          </p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-2 h-4 w-4 text-[#8892A3]" />
            <Input
              className="h-8 w-56 rounded-sm pl-8 text-xs"
              placeholder="Search name, phone or GSTIN"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button
            size="sm"
            className="h-8 rounded-sm"
            onClick={() => { setEditId(""); setForm(empty); setOpen(true); }}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add party
          </Button>
        </div>
      </div>
      <div className="border bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F1F3F6]">
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>GSTIN</TableHead>
              <TableHead>State</TableHead>
              <TableHead className="text-right">Opening balance</TableHead>
              <TableHead>Balance type</TableHead>
              <TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="py-2 text-xs font-semibold">
                  {r.name}
                  <div className="text-[10px] font-normal text-[#687386]">
                    {r.email}
                  </div>
                </TableCell>
                <TableCell className="py-2 text-xs">{r.party_type}</TableCell>
                <TableCell className="py-2 text-xs">{r.phone || "—"}</TableCell>
                <TableCell className="py-2 font-mono text-xs">
                  {r.gstin || "Unregistered"}
                </TableCell>
                <TableCell className="py-2 text-xs">{r.state || "—"}</TableCell>
                <TableCell className="py-2 text-right font-mono text-xs">
                  {money(r.opening_balance)}
                </TableCell>
                <TableCell className="py-2 text-xs">{r.balance_type}</TableCell>
                <TableCell className="py-2"><div className="flex"><Button size="sm" variant="ghost" className="h-7" onClick={() => editParty(r)}><Pencil className="h-3.5 w-3.5" /></Button><Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => removeParty(r)}><Trash2 className="h-3.5 w-3.5" /></Button></div></TableCell>
              </TableRow>
            ))}
            {!visible.length && (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="py-12 text-center text-xs text-[#687386]"
                >
                  No customers or suppliers added.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-sm">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Customer / Supplier" : "Add Customer / Supplier"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-3">
            <Field label="Party type">
              <select
                className="mt-1 h-10 w-full border bg-white px-2 text-sm"
                value={form.party_type}
                onChange={(e) =>
                  setForm({
                    ...form,
                    party_type: e.target.value,
                    balance_type:
                      e.target.value === "SUPPLIER" ? "PAYABLE" : "RECEIVABLE",
                  })
                }
              >
                <option value="CUSTOMER">Customer</option>
                <option value="SUPPLIER">Supplier</option>
                <option value="BOTH">Customer & Supplier</option>
              </select>
            </Field>
            <Field label="Business / customer name">
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>
            <Field label="Mobile number">
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </Field>
            <Field label="Email">
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </Field>
            <Field label="GSTIN (optional)">
              <Input
                maxLength={15}
                className="uppercase"
                value={form.gstin}
                onChange={(e) => {
                  const gstin = e.target.value.toUpperCase();
                  setForm({
                    ...form,
                    gstin,
                    state: stateFromGstin(gstin) || form.state,
                  });
                }}
              />
            </Field>
            <Field label="State">
              <Input
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
              />
            </Field>
            <div className="col-span-2">
              <Field label="Billing address">
                <Input
                  value={form.address}
                  onChange={(e) =>
                    setForm({ ...form, address: e.target.value })
                  }
                />
              </Field>
            </div>
            <Field label="Opening balance">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.opening_balance}
                onChange={(e) =>
                  setForm({ ...form, opening_balance: e.target.value })
                }
              />
            </Field>
            <Field label="Opening balance type">
              <select
                className="mt-1 h-10 w-full border bg-white px-2 text-sm"
                value={form.balance_type}
                onChange={(e) =>
                  setForm({ ...form, balance_type: e.target.value })
                }
              >
                <option value="RECEIVABLE">Receivable from party</option>
                <option value="PAYABLE">Payable to party</option>
              </select>
            </Field>
            <DialogFooter className="col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">Save party</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SalonBookSettings() {
  const [form, setForm] = useState({
    legal_name: "",
    gstin: "",
    state: "",
    gst_registered: false,
    address: "",
    pan: "",
    books_locked_through: "",
    service_gst_rate: 0,
  });
  useEffect(() => {
    api
      .get("/client/salon/books/settings")
      .then((r) =>
        setForm(
          r.data || {
            legal_name: "",
            gstin: "",
            state: "",
            gst_registered: false,
            address: "",
            pan: "",
            books_locked_through: "",
            service_gst_rate: 0,
          },
        ),
      )
      .catch((e) => toast.error(formatErr(e.response?.data?.detail)));
  }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      const r = await api.put("/client/salon/books/settings", form);
      setForm(r.data);
      toast.success("Accounting and GST setup saved");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };
  return (
    <div className="max-w-3xl border bg-white">
      <div className="border-b bg-[#F1F3F6] px-3 py-2">
        <h2 className="text-sm font-bold">Accounting & GST Setup</h2>
        <p className="text-[10px] text-[#687386]">
          These settings control automatic intra-state and inter-state tax
          calculation.
        </p>
      </div>
      <form onSubmit={save} className="grid grid-cols-2 gap-3 p-4">
        <Field label="Legal business name">
          <Input
            value={form.legal_name}
            onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
          />
        </Field>
        <Field label="Registered state">
          <Input
            required
            value={form.state}
            placeholder="e.g. Maharashtra"
            onChange={(e) => setForm({ ...form, state: e.target.value })}
          />
        </Field>
        <Field label="GST registration">
          <select
            className="mt-1 h-10 w-full border bg-white px-2 text-sm"
            value={form.gst_registered ? "yes" : "no"}
            onChange={(e) =>
              setForm({ ...form, gst_registered: e.target.value === "yes" })
            }
          >
            <option value="no">Not registered under GST</option>
            <option value="yes">GST registered</option>
          </select>
        </Field>
        <Field label="GSTIN">
          <Input
            disabled={!form.gst_registered}
            maxLength={15}
            className="uppercase"
            value={form.gstin}
            onChange={(e) => {
              const gstin = e.target.value.toUpperCase();
              setForm({
                ...form,
                gstin,
                state: stateFromGstin(gstin) || form.state,
              });
            }}
          />
        </Field>
        <Field label="PAN">
          <Input maxLength={10} className="uppercase" value={form.pan || ""} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} />
        </Field>
        <Field label="Books locked through">
          <Input type="date" value={form.books_locked_through || ""} onChange={(e) => setForm({ ...form, books_locked_through: e.target.value })} />
        </Field>
        <Field label="Default GST on salon services">
          <select disabled={!form.gst_registered} className="mt-1 h-10 w-full border bg-white px-2 text-sm" value={form.service_gst_rate || 0} onChange={(e) => setForm({ ...form, service_gst_rate: Number(e.target.value) })}>{[0, 5, 12, 18, 28].map((rate) => <option key={rate} value={rate}>{rate}%</option>)}</select>
        </Field>
        <div className="col-span-2"><Field label="Registered address"><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field></div>
        <div className="col-span-2 flex justify-end">
          <Button type="submit">Save setup</Button>
        </div>
      </form>
    </div>
  );
}

const reportColumns = {
  "general-ledger": [["date", "Date"], ["voucher_number", "Voucher"], ["ledger", "Ledger"], ["party", "Party"], ["debit", "Debit"], ["credit", "Credit"]],
  "day-book": [["voucher_date", "Date"], ["voucher_number", "Voucher"], ["voucher_type", "Type"], ["party_name", "Party"], ["narration", "Narration"], ["amount", "Amount"]],
  "cash-book": [["date", "Date"], ["voucher_number", "Voucher"], ["party", "Party"], ["narration", "Narration"], ["debit", "Receipt"], ["credit", "Payment"]],
  "bank-book": [["date", "Date"], ["voucher_number", "Voucher"], ["ledger", "Bank"], ["party", "Party"], ["debit", "Deposit"], ["credit", "Withdrawal"]],
  "bank-reconciliation": [["date", "Date"], ["voucher_number", "Voucher"], ["ledger", "Bank"], ["party", "Party"], ["debit", "Debit"], ["credit", "Credit"], ["bank_reconciled", "Reconciliation"]],
  "audit-trail": [["created_at", "Timestamp"], ["action", "Action"], ["voucher_number", "Voucher"], ["party_name", "Party"], ["performed_by", "User"]],
};

function exportRows(filename, rows, columns) {
  const safe = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const csv = [columns.map(([, label]) => safe(label)).join(","), ...rows.map((row) => columns.map(([key]) => safe(row[key])).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a"); anchor.href = url; anchor.download = filename; anchor.click(); URL.revokeObjectURL(url);
}

function AmountList({ title, rows = [], total }) {
  return <section className="border bg-white"><div className="border-b bg-[#F1F3F6] px-3 py-2 text-xs font-bold">{title}</div><div className="divide-y">{rows.map((row) => <div key={row.id || row.code} className="flex justify-between px-3 py-2 text-xs"><span>{row.name}</span><strong className="font-mono">{money(row.amount)}</strong></div>)}{!rows.length && <div className="p-6 text-center text-xs text-[#687386]">No balance</div>}</div><div className="flex justify-between border-t bg-[#F8FAFC] px-3 py-2 text-sm font-bold"><span>Total</span><span className="font-mono">{money(total)}</span></div></section>;
}

function ReportPage({ reportName, title }) {
  const [data, setData] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState(today());
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get(`/client/salon/books/reports/${reportName}`, { params: { from_date: fromDate || undefined, to_date: toDate || undefined } }); setData(r.data || {}); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); } finally { setBusy(false); }
  }, [reportName, fromDate, toDate]);
  useEffect(() => { load(); }, [load]);
  const columns = reportColumns[reportName] || [];
  const rows = data?.rows || [];
  const gst = data?.gst || {};
  const download = () => {
    if (reportName === "ca-access") {
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })); const a = document.createElement("a"); a.href = url; a.download = `salon-ca-package-${toDate}.json`; a.click(); URL.revokeObjectURL(url); return;
    }
    const exportable = columns.length ? rows : reportName.startsWith("gstr-2") ? data?.purchases || [] : data?.sales || [];
    const exportColumns = columns.length ? columns : [["voucher_date", "Date"], ["voucher_number", "Voucher"], ["party_name", "Party"], ["party_gstin", "GSTIN"], ["place_of_supply", "Place of Supply"], ["taxable_value", "Taxable"], ["cgst", "CGST"], ["sgst", "SGST"], ["igst", "IGST"], ["amount", "Total"]];
    exportRows(`${reportName}-${toDate}.csv`, exportable, exportColumns);
  };
  const reconcile = async (row) => {
    try { await api.post(`/client/salon/books/vouchers/${row.id}/reconcile`, null, { params: { reconciliation_date: today() } }); toast.success("Bank entry reconciled"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const isGst = ["gst", "gstr-1", "gstr-3b", "gstr-2b"].includes(reportName);
  return <>
    <div className="mb-3 flex flex-wrap items-end justify-between gap-2"><div><h2 className="text-lg font-bold">{title}</h2><p className="text-xs text-[#687386]">Salon books · posted vouchers only</p></div><div className="flex flex-wrap items-end gap-2"><Field label="From"><Input type="date" className="h-8 w-36 text-xs" value={fromDate} onChange={(e) => setFromDate(e.target.value)} /></Field><Field label="To"><Input type="date" className="h-8 w-36 text-xs" value={toDate} onChange={(e) => setToDate(e.target.value)} /></Field><Button size="sm" variant="outline" className="h-8" onClick={load} disabled={busy}><RefreshCw className="mr-1 h-3.5 w-3.5" />Refresh</Button><Button size="sm" variant="outline" className="h-8" onClick={download}><Download className="mr-1 h-3.5 w-3.5" />Export</Button><Button size="sm" className="h-8" onClick={() => window.print()}><Printer className="mr-1 h-3.5 w-3.5" />Print</Button></div></div>
    {reportName === "profit-loss" && <div className="grid gap-3 md:grid-cols-2"><AmountList title="Income" rows={data?.income} total={data?.total_income} /><AmountList title="Expenses" rows={data?.expenses} total={data?.total_expenses} /><div className="md:col-span-2 flex justify-between border bg-[#172033] px-4 py-3 text-white"><strong>Net profit / (loss)</strong><strong className="font-mono">{money(data?.net_profit)}</strong></div></div>}
    {reportName === "balance-sheet" && <div className="grid gap-3 md:grid-cols-2"><AmountList title="Assets" rows={data?.assets} total={data?.total_assets} /><AmountList title="Liabilities & Equity" rows={[...(data?.liabilities || []), { id: "profit", name: "Current period profit / (loss)", amount: data?.current_profit }]} total={data?.total_liabilities} /></div>}
    {isGst && <><div className="grid grid-cols-2 border bg-white md:grid-cols-5">{[["Taxable sales", gst.taxable_sales], ["Output GST", gst.output_tax], ["Taxable purchases", gst.taxable_purchases], ["Input GST", gst.input_tax], ["Net payable", gst.net_payable]].map(([label, value]) => <div key={label} className="border-r p-3"><div className="text-[9px] uppercase text-[#687386]">{label}</div><div className="font-mono text-lg font-bold">{money(value)}</div></div>)}</div><div className="mt-3 border bg-white"><Table><TableHeader><TableRow className="bg-[#F1F3F6]"><TableHead>Date</TableHead><TableHead>Voucher</TableHead><TableHead>Party / GSTIN</TableHead><TableHead>Place</TableHead><TableHead className="text-right">Taxable</TableHead><TableHead className="text-right">CGST</TableHead><TableHead className="text-right">SGST</TableHead><TableHead className="text-right">IGST</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader><TableBody>{(reportName === "gstr-2b" ? data?.purchases : data?.sales || []).map((r) => <TableRow key={r.id}><TableCell className="text-xs">{r.voucher_date}</TableCell><TableCell className="text-xs font-semibold">{r.voucher_number}</TableCell><TableCell className="text-xs">{r.party_name || "—"}<div className="font-mono text-[10px] text-[#687386]">{r.party_gstin}</div></TableCell><TableCell className="text-xs">{r.place_of_supply}</TableCell><TableCell className="text-right font-mono text-xs">{money(r.taxable_value)}</TableCell><TableCell className="text-right font-mono text-xs">{money(r.cgst)}</TableCell><TableCell className="text-right font-mono text-xs">{money(r.sgst)}</TableCell><TableCell className="text-right font-mono text-xs">{money(r.igst)}</TableCell><TableCell className="text-right font-mono text-xs font-bold">{money(r.amount)}</TableCell></TableRow>)}</TableBody></Table></div><p className="mt-2 text-[10px] text-amber-700">GST working paper: reconcile with GST portal data and obtain CA review before filing.</p></>}
    {reportName === "ca-access" && <div className="grid grid-cols-2 border bg-white md:grid-cols-4">{[["Vouchers", data?.summary?.voucher_count], ["Debits", data?.summary?.debits], ["Credits", data?.summary?.credits], ["Net profit", data?.summary?.net_profit]].map(([label, value]) => <div className="border-r p-4" key={label}><div className="text-[10px] uppercase text-[#687386]">{label}</div><div className="font-mono text-xl font-bold">{label === "Vouchers" ? value || 0 : money(value)}</div></div>)}<div className="col-span-full border-t p-5 text-xs text-[#566176]">Use Export to download the complete Salon ledger, voucher and GST package for your CA. Data remains tenant-isolated and does not use Society accounts.</div></div>}
    {columns.length > 0 && <div className="overflow-x-auto border bg-white"><Table><TableHeader><TableRow className="bg-[#F1F3F6]">{columns.map(([, label]) => <TableHead key={label} className={/[Dd]ebit|[Cc]redit|[Aa]mount|[Rr]eceipt|[Pp]ayment|[Dd]eposit|[Ww]ithdrawal/.test(label) ? "text-right" : ""}>{label}</TableHead>)}</TableRow></TableHeader><TableBody>{rows.map((row, i) => <TableRow key={`${row.id || row.voucher_number}-${i}`}>{columns.map(([key, label]) => <TableCell key={key} className={`${/[Dd]ebit|[Cc]redit|[Aa]mount|[Rr]eceipt|[Pp]ayment|[Dd]eposit|[Ww]ithdrawal/.test(label) ? "text-right font-mono" : ""} py-2 text-xs`}>{key === "bank_reconciled" ? (row.bank_reconciled ? <span className="font-semibold text-emerald-700">Reconciled {row.reconciliation_date}</span> : <Button size="sm" className="h-7" onClick={() => reconcile(row)}>Mark reconciled</Button>) : /[Dd]ebit|[Cc]redit|[Aa]mount|[Rr]eceipt|[Pp]ayment|[Dd]eposit|[Ww]ithdrawal/.test(label) ? money(row[key]) : row[key] || "—"}</TableCell>)}</TableRow>)}{!rows.length && <TableRow><TableCell colSpan={columns.length} className="py-12 text-center text-xs text-[#687386]">No posted transactions in this period.</TableCell></TableRow>}</TableBody></Table>{["cash-book", "bank-book", "bank-reconciliation"].includes(reportName) && <div className="flex justify-end border-t bg-[#F8FAFC] px-4 py-3 text-sm font-bold">Closing balance: <span className="ml-3 font-mono">{money(data?.closing_balance)}</span></div>}</div>}
  </>;
}

export default function SalonAccounting() {
  return (
    <AccountingChrome>
      <Routes>
        <Route index element={<Navigate to={`${BASE}/dashboard`} replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="ledgers" element={<Ledgers />} />
        <Route path="parties" element={<Parties />} />
        <Route path="settings" element={<SalonBookSettings />} />
        <Route path="trial-balance" element={<TrialBalance />} />
        {Object.keys(voucherMap).map((k) => (
          <Route key={k} path={k} element={<VoucherPage kind={k} />} />
        ))}
        {groups
          .flatMap(([, i]) => i)
          .filter(
            ([, p]) =>
              ![
                "dashboard",
                "ledgers",
                "parties",
                "settings",
                "trial-balance",
                ...Object.keys(voucherMap),
              ].includes(p),
          )
          .map(([l, p]) => (
            <Route key={p} path={p} element={<ReportPage reportName={p} title={l} />} />
          ))}
        <Route
          path="*"
          element={<Navigate to={`${BASE}/dashboard`} replace />}
        />
      </Routes>
    </AccountingChrome>
  );
}
